import React, { useEffect, useMemo, useState } from "react";
import { useDispatch } from "react-redux";
import { ExternalLink } from "lucide-react";
import {
  activeInfluencer,
  getAllEmployeeList,
  getExamListNew,
  getParentCourseNew,
} from "../../../Action/courseAction";
import {
  CustomAdminTable,
  CustomBadge,
  CustomIconButton,
  CustomPageLayout,
  useHeaderFilterButton,
} from "../../../Component/customComponents";
import { CourseLines, formatINR, maskMobile } from "../../Common/transactionCommon";

const INFLUENCER_CATEGORY_LIST_ID = "65f340e776a56e01d9858028";
const EMPTY_FILTERS = { exam: "", examStream: "", category: "" };

/** "1500" → ₹1,500.00; text like "1.5k" stays as typed */
const formatPrice = (v) =>
  v === undefined || v === null || v === ""
    ? null
    : Number.isFinite(Number(v))
      ? formatINR(v)
      : String(v);

const GetInfluencer = () => {
  const dispatch = useDispatch();

  const headerFilter = useHeaderFilterButton(); // header Filters button → the table's drawer

  const [filters, setFilters] = useState(EMPTY_FILTERS);
  const [examList, setExamList] = useState([]);
  const [examStreamList, setExamStreamList] = useState([]);
  const [categoryList, setCategoryList] = useState([]);

  const [influencerList, setInfluencerList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchText, setSearchText] = useState("");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  /* ---------- API (activeInfluencer always calls back, even on errors) ---------- */
  const fetchInfluencers = (f = filters) => {
    const reqBody = { baseCourseId: f.examStream?._id, examTypeId: f.exam?._id };
    // employee-list items use `id` (see manageEmployee); fall back to `_id`
    if (f.category) reqBody.categoryId = f.category?.id ?? f.category?._id;
    setLoading(true);
    dispatch(
      activeInfluencer(reqBody, (resp) => {
        setInfluencerList(Array.isArray(resp) ? resp : []);
        setPage(1);
        setLoading(false);
      }),
    );
  };

  const loadStreams = (exam) => {
    setExamStreamList([]);
    if (!exam) return;
    dispatch(getParentCourseNew(`${exam._id}`, (res) => setExamStreamList(res?.length > 0 ? res : [])));
  };

  useEffect(() => {
    dispatch(getExamListNew((res) => setExamList(res?.length > 0 ? res : [])));
    dispatch(getAllEmployeeList(INFLUENCER_CATEGORY_LIST_ID, (resp) => setCategoryList(resp || [])));
    fetchInfluencers(EMPTY_FILTERS); // all active influencers on open, as before
  }, []);

  /* ---------- filter drawer (rendered by CustomAdminTable) ---------- */
  const filterFields = useMemo(
    () => [
      {
        key: "exam",
        label: "Exam Name",
        type: "dropdown",
        options: examList,
        clears: ["examStream"],
      },
      {
        key: "examStream",
        label: "Exam Stream",
        type: "dropdown",
        options: examStreamList,
        disabled: (v) => !v.exam,
        hint: (v) => (!v.exam ? "Select an exam first" : undefined),
      },
      { key: "category", label: "Category", type: "dropdown", options: categoryList },
    ],
    [examList, examStreamList, categoryList],
  );

  /* ---------- table ---------- */
  const filteredRows = useMemo(() => {
    const term = searchText.trim().toLowerCase();
    if (!term) return influencerList;
    return influencerList.filter((row) =>
      [
        row?.name,
        row?.mobileNo,
        row?.examTypeId?.name,
        row?.baseCourseId?.[0]?.name,
        ...(row?.platformDetails || []).map((p) => p?.name),
      ]
        .filter(Boolean)
        .some((v) => String(v).toLowerCase().includes(term)),
    );
  }, [influencerList, searchText]);

  const pagedRows = useMemo(
    () => filteredRows.slice((page - 1) * pageSize, page * pageSize),
    [filteredRows, page, pageSize],
  );

  // One line per platform in each platform column (replaces the old rowSpan layout).
  // Same fixed line height everywhere, so a platform reads straight across the row.
  const platformColumn = (key, header, pick, opts = {}) => ({
    key,
    header,
    minWidth: 110,
    ...opts,
    render: (_, row) => (
      <CourseLines courses={row?.platformDetails} pick={pick} lineHeight={30} align={opts.align} />
    ),
  });

  const columns = [
    { key: "name", header: "Name", minWidth: 160, render: (v) => <strong>{v || "—"}</strong> },
    { key: "mobileNo", header: "Mob No.", minWidth: 120, render: maskMobile },
    { key: "exam", header: "Exam Name", minWidth: 120, render: (_, row) => row?.examTypeId?.name || "—" },
    {
      key: "stream",
      header: "Exam Stream",
      minWidth: 130,
      render: (_, row) => row?.baseCourseId?.[0]?.name || "—",
    },
    platformColumn("platform", "Platform", (p) =>
      p?.name ? (
        <CustomBadge tone="info" size="sm">
          {p.name}
        </CustomBadge>
      ) : (
        "—"
      ),
    ),
    platformColumn("price", "Per Post Price", (p) => formatPrice(p?.perPostPrice) || "—", { align: "right" }),
    platformColumn("frequency", "Frequency", (p) => p?.frequency || "—", { align: "center" }),
    platformColumn("followers", "Followers", (p) => p?.followers || "—", { align: "right" }),
    platformColumn(
      "link",
      "Link",
      (p) => (
        <CustomIconButton
          icon={<ExternalLink size={14} />}
          tone="primary"
          size="sm"
          title={p?.link ? `Open ${p?.name || "link"}` : "No link"}
          href={p?.link || undefined}
          target="_blank"
          disabled={!p?.link}
        />
      ),
      { align: "center", minWidth: 70 },
    ),
  ];

  return (
    <CustomPageLayout title="INFLUENCER" fill headerActions={headerFilter.button}>
      <CustomAdminTable
        {...headerFilter.tableProps}
        hideFilterButton
        id="influencer-table"
        title={`Active Influencers (${influencerList.length})`}
        columns={columns}
        data={pagedRows}
        loading={loading}
        rowKey={(row, index) => row?._id || row?.mobileNo || index}
        enableSerialNumber
        serialHeader="S.No"
        search={{
          value: searchText,
          onChange: (v) => {
            setSearchText(v);
            setPage(1);
          },
          placeholder: "Search name, mobile, exam or platform...",
        }}
        advancedFilters={filterFields}
        advancedFilterValues={filters}
        onApplyFilters={(values) => {
          setFilters(values);
          fetchInfluencers(values);
        }}
        applyFiltersOnReset
        onFilterChange={(key, value) => key === "exam" && loadStreams(value)}
        onFilterDrawerOpen={(applied) => loadStreams(applied.exam)}
        filterSubtitle="Filter by exam, stream or category"
        pagination={{
          page,
          pageSize,
          total: filteredRows.length,
          onPageChange: setPage,
          onPageSizeChange: (size) => {
            setPageSize(size);
            setPage(1);
          },
        }}
        emptyTitle={searchText ? "No matching influencers" : "No influencers"}
        emptyDescription={searchText ? "Try a different search." : "No active influencers for these filters."}
        maxHeight="83vh"
      />
    </CustomPageLayout>
  );
};

export default GetInfluencer;
